import { SubmitHandler, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getEmail,
  getFirstName,
  getSessionId,
  getTableNumber,
  updateEmail,
  updateFirstName,
  updateMarketingOptIn,
  getCreateResult,
  getName,
} from "../redux/slices";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../redux/hooks";
import { useSearchParams } from "react-router-dom";
import { Loading } from "./Loading";
import { trackEvent } from "../utils/analytics";
import { setStoryOptInCookie } from "../utils/cookies";

import "./playerEntryDetails.scss";
import { CaptureEmailDto } from "../types/player";

type FormValues = {
  email: string;
  firstName: string;
  lastName: string;
  marketingOptIn: boolean;
};

export const NameAndEmail: React.FC = () => {
  const { register, handleSubmit, setValue, watch } = useForm<FormValues>({
    defaultValues: { marketingOptIn: true },
  });
  const [loading, setLoading] = useState<boolean>(true);
  const hasStartedTypingName = useRef<boolean>(false);
  const hasStartedTypingEmail = useRef<boolean>(false);
  const hasFocusedName = useRef<boolean>(false);
  const hasFocusedEmail = useRef<boolean>(false);
  const [searchParams] = useSearchParams();
  const email = useSelector(getEmail);
  const firstName = useSelector(getFirstName);
  const playerName = useSelector(getName);
  const dispatch = useAppDispatch();
  const nav = useNavigate();

  // check if the player was created successfully, before giving the option to add an email address
  const createResult = useSelector(getCreateResult);
  const tableNumberString = createResult?.reason?.replace("SHUFFLEDECK", "");
  const tableNumberResult = parseInt(tableNumberString);

  // if the redux store is missing a session ID (or the session ID does not match the URL parameter),
  // then redirect to the start page. Suspect that this is a shared link and this "new" user needs to start at the beginning of the journey
  let sessionId = searchParams.get("s");
  let tableNumber = searchParams.get("t");
  let venueCode = searchParams.get("v");
  const sessionIdRedux = useSelector(getSessionId);
  const tableNumberRedux = useSelector(getTableNumber);

  useEffect(() => {
    setValue("email", email, { shouldValidate: true });
    setValue("firstName", firstName, { shouldValidate: true });
  }, [email, firstName, setValue]);

  useEffect(() => {
    if (sessionIdRedux === null || tableNumberRedux === null) {
      nav(`/?s=${sessionId}&t=${tableNumber}&v=${venueCode}`);
    }

    if (sessionId !== sessionIdRedux || tableNumber !== tableNumberRedux) {
      nav(`/?s=${sessionId}&t=${tableNumber}&v=${venueCode}`);
    } else {
      setLoading(false);
    }
  }, [sessionId, tableNumber, venueCode, sessionIdRedux, tableNumberRedux, nav]);

  // END of "copied link" checking

  const firstNameValue = watch("firstName", "");
  const emailValue = watch("email", "");

  useEffect(() => {
    if (firstNameValue?.length > 0 && !hasStartedTypingName.current) {
      hasStartedTypingName.current = true;
      trackEvent({
        gaEvent: "start_typing_name",
        clarityEvent: "start_typing_name",
        params: { venue_code: venueCode },
      });
    }
  }, [firstNameValue, venueCode]);

  useEffect(() => {
    if (emailValue?.length > 0 && !hasStartedTypingEmail.current) {
      hasStartedTypingEmail.current = true;
      trackEvent({
        gaEvent: "start_typing_email",
        clarityEvent: "start_typing_email",
        params: { venue_code: venueCode },
      });
    }
  }, [emailValue, venueCode]);

  useEffect(() => {
    setValue("email", email, { shouldValidate: true });
    setValue("firstName", firstName, { shouldValidate: true });
  }, [email, firstName, setValue]);

  const { onChange: firstNameOnChange, ...firstNameRegister } = register("firstName");
  const { onChange: emailOnChange, ...emailRegister } = register("email", {
    validate: (value) =>
      !value ||
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        value,
      ) ||
      "Please enter a valid email",
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const hasEmail = data.email && data.email.trim().length > 0;

    setStoryOptInCookie(!!hasEmail);

    // trackEvent({
    //   gaEvent: "marketing_opt_in",
    //   clarityEvent: "marketing_opt_in",
    //   params: {
    //     venue_code: venueCode,
    //     marketing_opt_in: data.marketingOptIn ? "allowed" : "not_allowed",
    //   },
    // });

    if (hasEmail) {
      trackEvent({
        gaEvent: "click_receive_story",
        clarityEvent: "click_receive_story",
        params: { venue_code: venueCode },
      });
      dispatch(updateEmail(data.email));
      dispatch(updateFirstName(data.firstName));
      dispatch(updateMarketingOptIn(data.marketingOptIn));

      const playerDTO: CaptureEmailDto = {
        Email: data.email,
        FirstName: data.firstName,
        LastName: data.lastName,
        MarketingOptIn: data.marketingOptIn,
        PlayerId: createResult.playerUid,
      };

      setLoading(true);
      const region = process.env.REACT_APP_REGION;

      try {
        const response: Response = await fetch(
          `/api/v1/${region}/electricshuffle/sessions/${sessionId}/emails`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(playerDTO),
          },
        );
        if (response.ok) {
          trackEvent({
            gaEvent: "EmailCaptured",
            params: { venue_code: venueCode },
          });
        }
      } catch (error) {
        // continue regardless
      }
      setLoading(false);
    }

    nav(`/complete?s=${sessionId}&t=${tableNumber}&v=${venueCode}`);
  };

  const isTableSuccess = !isNaN(tableNumberResult);
  const isLobby = createResult.reason === "LOBBY";

  const ukNow = new Date();
  const ukHour = parseInt(
    new Intl.DateTimeFormat("en-GB", {
      hour: "numeric",
      hour12: false,
      timeZone: "Europe/London",
    }).format(ukNow),
    10,
  );
  const ukMinute = parseInt(
    new Intl.DateTimeFormat("en-GB", {
      minute: "numeric",
      timeZone: "Europe/London",
    }).format(ukNow),
    10,
  );
  const isUk = window.location.hostname === "play.electricshuffle.com";
  const ukDay = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    timeZone: "Europe/London",
  });
  const isUkSaturday = ukDay === "Saturday";
  const showStoryForm =
    !isUk || isUkSaturday || ukHour < 10 || ukHour > 15 || (ukHour === 15 && ukMinute >= 30);

  useEffect(() => {
    if (!loading && (isTableSuccess || isLobby) && !showStoryForm) {
      nav(`/complete?s=${sessionId}&t=${tableNumber}&v=${venueCode}`);
    }
  }, [loading, isTableSuccess, isLobby, showStoryForm, nav, sessionId, tableNumber, venueCode]);

  if (loading) return <Loading />;

  if ((isTableSuccess || isLobby) && !showStoryForm) {
    return <Loading />;
  }

  if ((isTableSuccess || isLobby) && showStoryForm) {
    return (
      <div className="pageContainer">
        <div className="logoContainer">
          <img src={"./img/es-logo.svg"} width="110px" alt="Electric Shuffle" />
        </div>
        <div className="contentCard">
          <div className="formContainer">
            <>
              <form data-clarity-mask="true" id="storyForm" onSubmit={handleSubmit(onSubmit)}>
                <div className="formHeader">
                  <div className="step">STEP 3/3</div>
                  <div className="title">
                    GET YOUR STORY, <br /> {playerName}
                  </div>
                </div>
                <p className="formSubtitle">
                  Relive the game. Share the best bits. Get your highlights and replays by email.
                </p>
                <input
                  type="text"
                  placeholder="Enter first name"
                  autoComplete="given-name"
                  {...firstNameRegister}
                  onChange={(e) => {
                    e.target.value = e.target.value.replace(
                      /[\p{Extended_Pictographic}\p{Emoji_Modifier}\u200D\uFE0F\u20E3]|\p{Regional_Indicator}{2}/gu,
                      "",
                    );
                    firstNameOnChange(e);
                  }}
                  onFocus={() => {
                    if (!hasFocusedName.current) {
                      hasFocusedName.current = true;
                      trackEvent({
                        clarityEvent: "click_in_name_field",
                        params: { venue_code: venueCode },
                      });
                    }
                  }}
                />
                <input
                  type="text"
                  placeholder="Enter email"
                  className="emailInput"
                  autoComplete="email"
                  {...emailRegister}
                  onChange={(e) => {
                    e.target.value = e.target.value.replace(
                      /[\p{Extended_Pictographic}\p{Emoji_Modifier}\u200D\uFE0F\u20E3]|\p{Regional_Indicator}{2}/gu,
                      "",
                    );
                    emailOnChange(e);
                  }}
                  onFocus={() => {
                    if (!hasFocusedEmail.current) {
                      hasFocusedEmail.current = true;
                      trackEvent({
                        clarityEvent: "click_in_email_field",
                        params: { venue_code: venueCode },
                      });
                    }
                  }}
                />
                {/* <label className="marketingOptInLabel">
                    <input
                      type="checkbox"
                      className="marketingOptInCheckbox"
                      {...register("marketingOptIn")}
                    />
                    We'd also love to send you new and exclusive game offers from Electric Shuffle. Uncheck this
                    box if you do not want to receive these updates.
                  </label> */}
              </form>
              <div className="starsContainer stories">
                <img src={"./img/stars.svg"} alt="Stars" className="stars" />
              </div>
            </>
          </div>
        </div>
        <footer className="btn-container">
          <button className="small" type="submit" form="storyForm">
            DONE
          </button>
        </footer>
      </div>
    );
  }

  let errorTitle = "Error";
  let errorText = "Please use the touchscreen or talk to a host in venue";

  switch (createResult.reason) {
    case "Pending":
      errorText = "Taking longer than expected";
      break;
    case "TableFull":
      errorText = "Table is full, please talk to a host";
      break;
  }

  return (
    <div className="pageContainer">
      <div className="logoContainer">
        <img src={"./img/es-logo.svg"} width="110px" alt="Electric Shuffle" />
      </div>
      <div className="contentCard">
        <div className="errorContainer">
          <div className="errorTitleText">{errorTitle}</div>
          <div className="errorText">{errorText}</div>
          <div className="puckyBottomLine"></div>
          <img src={"./img/pucky.svg"} className="errorPucky" width="90px" alt="Electric Shuffle Pucky" />
        </div>
      </div>
    </div>
  );
};
